import React, { useState, useEffect } from "react";
import { isInternal } from "../../../utils";
import { useUser } from "../../../hooks/useUser";
import IconSearch from "../../../assets/svg/search-icon-grey.svg";
import ExpertSelectionCard from "./ExpertSelectionCard";
import ExpertSelectionList from "./ExpertSelectionList";
import ListingFilters from "../../../components/ListingFilters";
import { useSearchParams } from "react-router-dom";
import { DotLottleLoader } from "../../../components/Loader";
import { Grid, useMediaQuery, useTheme } from "@mui/material";
import { ViewToggle } from "../../../components/ViewToggle/ViewToggle";

const ExpertSection = ({
  pros,
  project,
  fetchProjectPros,
  onClearFilters,
  projectPros,
  setFilters,
  proCount,
  setSearchText,
  loading,
  // Per-(expert, product) algorithm scores for the search pool (internal only).
  algoProducts = /** @type {any[]} */ ([]),
  searchScoresByUserId = {},
  // Per-(expert, product) Industry/Use-Case labels — drives the badge on
  // browse cards and comes from the same lightweight search-scores endpoint.
  searchExpertTypesByUserId = {},
  // "Best match" sort is now driven SERVER-side by the parent (the pool is
  // ordered by the project-product algo score across the whole result set, not
  // just the current page). These props reflect/toggle that parent state.
  sortByScore = false,
  onToggleSortByScore,
  onRequestReopen = /** @type {any} */ (undefined),
}) => {
  const [view, setView] = useState("list");
  const [searchInput, setSearchInput] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const effectiveView = isMobile ? "list" : view;
  const { role } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();

  // The displayed list: drop already-selected experts. Ordering comes from the
  // server (the parent requests sort=-algoScore when sortByScore is on), so we
  // do NOT re-sort here — a client-side sort would only reorder the current page.
  const visiblePros = pros.filter(
    (expert) => !projectPros.some((p) => p.userid === expert.userid)
  );

  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    setSearchText(value);
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearchText("");
    setFilters({});
    const tabParam = searchParams.get('tab');
    setSearchParams(tabParam ? { tab: tabParam } : {});
    if (onClearFilters) onClearFilters();
  };

  const getGridClasses = (expertCount) => {
    if (expertCount === 1) return "grid-cols-1 max-w-[400px] mx-auto";
    if (expertCount === 2)
      return "grid-cols-1 sm:grid-cols-2 max-w-[800px] mx-auto";
    if (expertCount === 3)
      return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-[1200px] mx-auto";
    if (expertCount === 4)
      return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";
    return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4";
  };

  return (
    // No background here on purpose. Every expert row below is itself
    // `bg-surface` (ExpertSelectionList), so painting --surface on the
    // container too made the cards the same color as the thing containing
    // them — a flat 6000px slab where only the borders separated rows, in
    // BOTH themes. Letting the rows sit on the --page canvas is the same
    // card-on-canvas hierarchy the projects list already uses.
    <div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="w-full sm:w-auto relative">
          <img
            className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 left-5"
            src={IconSearch}
            alt="search-icon"
          />
          <input
            type="text"
            className="w-full sm:w-[333px] h-[40px] px-[2.25rem] py-0 bg-surface-2 text-text-2 placeholder-muted rounded-xl focus:outline-none focus:text-text-1"
            placeholder="Search"
            value={searchInput}
            onChange={onSearchChange}
          />
        </div>

        <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 w-full sm:w-auto mr-[12px]">
          {isInternal(role) && algoProducts.length > 0 && (
            <button
              type="button"
              onClick={() => onToggleSortByScore && onToggleSortByScore()}
              className={`h-[40px] px-3 rounded-xl text-[14px] font-medium font-circular border transition-colors whitespace-nowrap ${
                sortByScore
                  // #E4C9A1: the pale gold outline has no token (--accent-dark is
                  // darker); gold is theme-constant so the literal holds in both modes
                  ? "bg-tint text-accent-text border-[#E4C9A1]"
                  : "bg-surface-2 text-text-2 border-transparent hover:bg-track"
              }`}
              title="Sort experts by best algorithm match score across the project's products"
            >
              {sortByScore ? "✓ Best match" : "Sort: Best match"}
            </button>
          )}
          <ListingFilters
            shouldShowFilter={isInternal(role)}
            onSearchUpdate={setFilters}
            initialFilters={{}}
            type="experts"
            onClearFilters={clearFilters}
            // Industry/Use-Case needs the project's products to classify
            // against, so it only makes sense in this project-scoped pool.
            showExpertTypeFilter={isInternal(role) && algoProducts.length > 0}
          />
          {!isMobile && (
            <ViewToggle
              view={view}
              onViewChange={setView}
              gridValue="cards"
              listValue="list"
            />
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-6">
        <div className="flex items-center gap-2">
          <span className="text-text-1">{proCount} Experts</span>
        </div>
        <hr className="flex-grow border-t border-border-token ml-4" />
      </div>


      {effectiveView === "list" ? (
        <div className="my-[24px] space-y-4">
          {loading && pros.length === 0 ? (
            <DotLottleLoader />
          ) : pros.length === 0 ? (
            <div className="text-center text-muted mt-6">
              No experts available. Try clearing or updating your filters to expand your search.
            </div>
          ) : (
            visiblePros.map((expert) => (
              <ExpertSelectionList
                key={expert.id}
                recommendationCheck={false}
                pros={expert}
                project={project}
                onRequestReopen={onRequestReopen}
                onUpdate={fetchProjectPros}
                algoProducts={algoProducts}
                algoScores={searchScoresByUserId[String(expert.userid)]}
                algoExpertTypes={searchExpertTypesByUserId[String(expert.userid)]}
              />
            ))
          )}
        </div>
      ) : (
        <div
          className={`mt-[24px] grid ${getGridClasses(
            pros.length
          )} gap-6 w-full min-w-0`}
        >
          {loading && pros.length === 0 ? (
            <div className="col-span-full flex justify-center">
              <DotLottleLoader />
            </div>
          ) : pros.length === 0 ? (
            <div className="text-center col-span-full text-muted">
              No experts available. Try clearing or updating your filters to expand your search.
            </div>
          ) : (
            visiblePros.map((expert) => (
              <div key={expert.id} className="w-full min-w-0">
                <ExpertSelectionCard
                  isSelected={false}
                  recommendationCheck={false}
                  pro={expert}
                  project={project}
                  onRequestReopen={onRequestReopen}
                  onUpdate={fetchProjectPros}
                  onScoreChange={() => {}}
                  approvalCheck={false}
                  algoProducts={algoProducts}
                  algoScores={searchScoresByUserId[String(expert.userid)]}
                  algoExpertTypes={searchExpertTypesByUserId[String(expert.userid)]}
                />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ExpertSection;
